/**
 * Club communications — the admin's desk.
 *
 *   Compose    write to an audience, from a template or blank, with a live
 *              reachability count, merge fields, a preview, and test-to-self.
 *   Templates  the standard letters, editable and resettable.
 *   History    everything sent, with delivery rollups and drill-down.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_INVITED_MEMBER_TYPES,
  MEMBER_TYPES,
  MESSAGE_CATEGORIES,
  MESSAGE_CATEGORY_LABELS,
  eventAudienceSpec,
  memberTypeLabel,
  mergeFieldsUsed,
  MERGE_FIELDS,
  type AudienceRule,
  type AudienceSpec,
  type CommsTemplate,
  type ComposeMessageInput,
  type MemberType,
  type MessageCategory,
  type SendableChannel,
} from '@gwc/shared';
import { commsApi, type CommsEventOption } from '../api/comms';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useAuth } from '../auth/AuthProvider';
import { toast } from '../lib/toast';
import { Badge, Button, Select } from '../components/ui';
import { MergeFieldChips, insertAtCaret } from '../components/MergeFieldHelp';
import { TemplateEditor, TemplatePreview } from '../components/TemplateEditor';
import { MESSAGE_STATUS_LABEL, RollupLine } from '../components/DeliveryBadges';

type Tab = 'compose' | 'templates' | 'history';

export function Comms() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'compose';
  const setTab = (t: Tab) => setParams(t === 'compose' ? {} : { tab: t });

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-cream-50">Club communications</h1>
          <p className="mt-1 text-sm text-cream-300/70">
            Write once. It reaches the membership by push and email, always lands in their inbox,
            and every send is tracked here.
          </p>
        </div>
        <nav className="flex gap-1 rounded-lg border border-ink-800 p-1" aria-label="Communications">
          {(
            [
              ['compose', 'Compose'],
              ['templates', 'Templates'],
              ['history', 'History'],
            ] as [Tab, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                tab === id ? 'bg-gold-600 text-on-gold' : 'text-cream-200 hover:text-gold-200'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>

      {tab === 'compose' ? <Composer /> : tab === 'templates' ? <Templates /> : <History />}
    </div>
  );
}

// ---------------------------------------------------------------- Composer

type Segment =
  | { kind: 'all_members' }
  | { kind: 'member_type'; memberTypes: MemberType[] }
  | { kind: 'invited'; eventId: string }
  | { kind: 'not_rsvpd'; eventId: string }
  | { kind: 'rsvp'; eventId: string }
  | { kind: 'waitlist'; eventId: string }
  | { kind: 'unpaid'; eventId: string }
  | { kind: 'no_wine'; eventId: string }
  | { kind: 'attended'; eventId: string }
  | { kind: 'dues_outstanding' };

/** The presets leadership actually reaches for. Anything more exotic is rare. */
const SEGMENTS: { id: Segment['kind']; label: string; hint: string; needsEvent: boolean }[] = [
  {
    id: 'all_members',
    label: 'All members',
    hint: 'Every active member of the club — past members and those in memoriam are left out.',
    needsEvent: false,
  },
  {
    id: 'member_type',
    label: 'By member type',
    hint: 'Only the kinds of member you tick below.',
    needsEvent: false,
  },
  {
    id: 'invited',
    label: 'Invited to a tasting',
    hint: "Everyone of the member types the tasting is offered to, whether or not they've replied.",
    needsEvent: true,
  },
  {
    id: 'not_rsvpd',
    label: "Hasn't RSVP'd to a tasting",
    hint: 'Invited members who haven’t replied. Resolved when the message sends — anyone who RSVPs in the meantime drops out.',
    needsEvent: true,
  },
  { id: 'rsvp', label: 'Registered for a tasting', hint: 'Everyone holding a seat.', needsEvent: true },
  { id: 'waitlist', label: 'Waitlisted for a tasting', hint: 'Everyone waiting on a seat.', needsEvent: true },
  { id: 'unpaid', label: "Registered, hasn't paid", hint: 'Holding a seat with the payment still outstanding.', needsEvent: true },
  { id: 'no_wine', label: "Registered, hasn't entered a wine", hint: 'Holding a seat with no wine entered yet.', needsEvent: true },
  { id: 'attended', label: 'Attended a tasting', hint: 'Marked present on the night.', needsEvent: true },
  { id: 'dues_outstanding', label: 'Dues outstanding', hint: 'Members with an unpaid charge.', needsEvent: false },
];

/** In memoriam is never a recipient; everything else may be written to by type. */
const PICKABLE_MEMBER_TYPES = MEMBER_TYPES.filter((t) => t !== 'in_memoriam');

function toSpec(seg: Segment): AudienceSpec {
  switch (seg.kind) {
    case 'all_members':
      return { include: [{ type: 'all_members' }] };
    case 'member_type':
      return { include: [{ type: 'member_type', memberTypes: seg.memberTypes }] };
    case 'dues_outstanding':
      return { include: [{ type: 'dues_outstanding' } as AudienceRule] };
    case 'invited':
      return eventAudienceSpec('invited', seg.eventId);
    case 'not_rsvpd':
      return eventAudienceSpec('not_rsvpd', seg.eventId);
    case 'rsvp':
      return eventAudienceSpec('registered', seg.eventId);
    case 'waitlist':
      return eventAudienceSpec('waitlist', seg.eventId);
    case 'unpaid':
      return eventAudienceSpec('unpaid', seg.eventId);
    case 'no_wine':
      return eventAudienceSpec('no_wine', seg.eventId);
    case 'attended':
      return eventAudienceSpec('attended', seg.eventId);
  }
}

function Composer() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<MessageCategory>('announcements');
  const [channels, setChannels] = useState<SendableChannel[]>(['push', 'email']);
  const [segmentKind, setSegmentKind] = useState<Segment['kind']>('all_members');
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([...DEFAULT_INVITED_MEMBER_TYPES]);
  const [eventId, setEventId] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [lastFocus, setLastFocus] = useState<'subject' | 'body'>('body');

  const events = useQuery({ queryKey: ['comms', 'events'], queryFn: () => commsApi.events() });
  const templates = useQuery({ queryKey: ['comms', 'templates'], queryFn: () => commsApi.templates() });

  // "Use" on the Templates tab hands a template id over via sessionStorage.
  useEffect(() => {
    const handoff = sessionStorage.getItem('gwc-compose-template');
    if (handoff && templates.data) {
      sessionStorage.removeItem('gwc-compose-template');
      applyTemplate(handoff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.data]);

  const segmentDef = SEGMENTS.find((s) => s.id === segmentKind)!;
  const audienceReady =
    (!segmentDef.needsEvent || !!eventId) &&
    (segmentKind !== 'member_type' || memberTypes.length > 0);
  // Event merge fields resolve whenever a tasting is attached — even for an
  // "all members" send, so "Save the date" can go to everyone.
  const attachedEvent = eventId || undefined;

  const audience: AudienceSpec | null = useMemo(() => {
    if (!audienceReady) return null;
    if (segmentKind === 'member_type') return toSpec({ kind: 'member_type', memberTypes });
    return toSpec(
      segmentDef.needsEvent
        ? ({ kind: segmentKind, eventId } as Segment)
        : ({ kind: segmentKind } as Segment),
    );
  }, [segmentKind, eventId, audienceReady, segmentDef.needsEvent, memberTypes]);

  const debouncedAudience = useDebouncedValue(
    audience ? JSON.stringify({ audience, category, attachedEvent }) : '',
    300,
  );
  const preview = useQuery({
    queryKey: ['comms', 'preview', debouncedAudience],
    // The attached tasting applies its test clamp, exactly as the send will.
    queryFn: () => commsApi.previewAudience(audience!, category, attachedEvent),
    enabled: !!debouncedAudience && !!audience,
  });

  // The text preview resolves against the real tasting via the API — the same
  // code path the send uses — so what you see is what goes out.
  const debouncedText = useDebouncedValue(JSON.stringify({ title, body, attachedEvent }), 400);
  const textPreview = useQuery({
    queryKey: ['comms', 'text-preview', debouncedText],
    queryFn: () => commsApi.previewText(title, body, attachedEvent),
    enabled: !!(title || body),
  });
  const previewVars = {
    firstName: user?.firstName ?? 'James',
    lastName: user?.lastName ?? 'Whitmore',
    fullName: `${user?.firstName ?? 'James'} ${user?.lastName ?? 'Whitmore'}`,
  };

  const usesEventFields = mergeFieldsUsed(`${title}\n${body}`).some((k) =>
    MERGE_FIELDS.some((f) => f.key === k && f.scope === 'event'),
  );

  function applyTemplate(id: string) {
    setTemplateId(id);
    const t = templates.data?.find((x) => x.id === id);
    if (!t) return;
    setTitle(t.subject);
    setBody(t.body);
    setCategory(t.category as MessageCategory);
    setChannels(t.channels);
    if (t.scope === 'event' && t.eventAudience) {
      const map: Record<string, Segment['kind']> = {
        registered: 'rsvp',
        waitlist: 'waitlist',
        registered_and_waitlist: 'rsvp',
        not_rsvpd: 'not_rsvpd',
        attended: 'attended',
        all_members: 'all_members',
        invited: 'invited',
        unpaid: 'unpaid',
        no_wine: 'no_wine',
      };
      setSegmentKind(map[t.eventAudience] ?? 'all_members');
    }
  }

  const compose = (): ComposeMessageInput => ({
    templateId: templateId || undefined,
    title: title.trim(),
    body: body.trim(),
    category,
    channels,
    audience: audience!,
    eventId: attachedEvent,
    ...(scheduledFor ? { scheduledFor: new Date(scheduledFor).toISOString() } : {}),
  });

  const valid = title.trim().length > 0 && body.trim().length > 0 && !!audience;

  const sendMut = useMutation({
    mutationFn: () => commsApi.send(compose()),
    onSuccess: (r) => {
      toast.success(
        r.status === 'scheduled'
          ? `Scheduled for ${new Date(scheduledFor).toLocaleString()}.`
          : `Sent to ${r.recipientCount} member${r.recipientCount === 1 ? '' : 's'}.`,
      );
      setTitle('');
      setBody('');
      setScheduledFor('');
      setTemplateId('');
      void qc.invalidateQueries({ queryKey: ['comms', 'history'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send'),
  });

  const testMut = useMutation({
    mutationFn: () => commsApi.sendTest(compose()),
    onSuccess: (r) =>
      toast.success(`Test sent to you — ${r.sent.push} push, ${r.sent.email} email. Check both.`),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send the test'),
  });

  function toggleChannel(c: SendableChannel) {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }
  function insert(token: string) {
    if (lastFocus === 'subject') setTitle((t) => insertAtCaret(subjectRef.current, token, t));
    else setBody((b) => insertAtCaret(bodyRef.current, token, b));
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <section className="card space-y-5 p-6">
        {/* Start from a letter */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Start from a template"
            value={templateId}
            onChange={(e) => applyTemplate(e.target.value)}
            hint="Fills the subject and message — edit freely after."
          >
            <option value="">Blank message</option>
            <optgroup label="General">
              {(templates.data ?? [])
                .filter((t) => t.scope === 'general')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </optgroup>
            <optgroup label="About a tasting">
              {(templates.data ?? [])
                .filter((t) => t.scope === 'event')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </optgroup>
          </Select>
          <Select
            label="About which tasting"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            hint={
              usesEventFields && !eventId
                ? 'Your text uses tasting fields — pick one or they will be blank.'
                : 'Optional. Fills {{eventTitle}}, {{eventDate}} and friends; links the email to the tasting.'
            }
            error={usesEventFields && !eventId ? 'Tasting fields need a tasting' : null}
          >
            <option value="">No tasting</option>
            {(events.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {eventLabel(e)}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm text-cream-200" htmlFor="msg-title">
            Subject
          </label>
          <input
            id="msg-title"
            ref={subjectRef}
            className="field"
            placeholder="November tasting — Right Bank Bordeaux"
            maxLength={140}
            value={title}
            onFocus={() => setLastFocus('subject')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm text-cream-200" htmlFor="msg-body">
            Message
          </label>
          <textarea
            id="msg-body"
            ref={bodyRef}
            className="field min-h-[180px]"
            placeholder="Gentlemen — our November tasting will be held at…"
            maxLength={5000}
            value={body}
            onFocus={() => setLastFocus('body')}
            onChange={(e) => setBody(e.target.value)}
          />
          <p className="mt-1 text-xs text-cream-300/45">
            {body.length}/5000 · Plain text. Blank lines start a paragraph; links become clickable.
          </p>
        </div>

        <MergeFieldChips onInsert={insert} eventAttached={!!eventId} />

        {/* Audience */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Send to"
            value={segmentKind}
            onChange={(e) => setSegmentKind(e.target.value as Segment['kind'])}
            hint={segmentDef.hint}
          >
            {SEGMENTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </Select>
          {segmentDef.needsEvent && !eventId ? (
            <div className="self-end text-sm text-warning">
              Choose the tasting above to use this audience.
            </div>
          ) : null}
        </div>

        {segmentKind === 'member_type' ? (
          <fieldset className="rounded-lg border border-ink-800 p-4">
            <legend className="px-1 text-sm text-cream-200">Member types</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {PICKABLE_MEMBER_TYPES.map((t) => (
                <label key={t} className="flex cursor-pointer items-center gap-2 text-sm text-cream-200">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-gold-500"
                    checked={memberTypes.includes(t)}
                    onChange={() =>
                      setMemberTypes((prev) =>
                        prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                      )
                    }
                  />
                  {memberTypeLabel(t)}
                </label>
              ))}
            </div>
            {memberTypes.length === 0 ? (
              <p className="mt-2 text-xs text-warning">Tick at least one member type.</p>
            ) : null}
          </fieldset>
        ) : null}

        {/* Category + channels */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as MessageCategory)}
            hint="Members mute by category, so this decides who hears the ping."
          >
            {MESSAGE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {MESSAGE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
          <div>
            <span className="mb-1 block text-sm text-cream-200">Channels</span>
            <div className="flex gap-4 pt-2">
              {(['push', 'email'] as const).map((c) => (
                <label key={c} className="flex cursor-pointer items-center gap-2 text-sm text-cream-200">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-gold-500"
                    checked={channels.includes(c)}
                    onChange={() => toggleChannel(c)}
                  />
                  {c === 'push' ? 'Push' : 'Email'}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-cream-300/45">The inbox copy is always delivered.</p>
          </div>
        </div>

        {/* Reachability — the number that actually matters */}
        <div className="rounded-lg border border-ink-800 bg-ink-950/40 p-4">
          {!audienceReady ? (
            <p className="text-sm text-cream-300/50">Choose a tasting to see who this reaches.</p>
          ) : preview.isLoading ? (
            <p className="text-sm text-cream-300/50">Counting…</p>
          ) : preview.data ? (
            <>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <span className="font-display text-2xl text-cream-50">
                  {preview.data.count} member{preview.data.count === 1 ? '' : 's'}
                </span>
                {channels.includes('push') ? (
                  <span className="text-sm text-cream-300/70">
                    <strong className="text-gold-400">{preview.data.reachable.push}</strong> by push
                  </span>
                ) : null}
                {channels.includes('email') ? (
                  <span className="text-sm text-cream-300/70">
                    <strong className="text-gold-400">{preview.data.reachable.email}</strong> by email
                  </span>
                ) : null}
              </div>
              {preview.data.count > 0 &&
              channels.length > 0 &&
              preview.data.reachable.push + preview.data.reachable.email === 0 ? (
                <p className="mt-2 text-sm text-wine-300">
                  Nobody in this audience can be reached on the selected channels. They'll still get
                  an inbox message.
                </p>
              ) : null}
              {preview.data.sample.length ? (
                <p className="mt-2 text-xs text-cream-300/45">
                  {preview.data.sample
                    .slice(0, 6)
                    .map((p) => `${p.firstName} ${p.lastName}`)
                    .join(', ')}
                  {preview.data.count > 6 ? ` and ${preview.data.count - 6} more` : ''}
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        {/* Schedule */}
        <div>
          <label className="mb-1 block text-sm text-cream-200" htmlFor="msg-when">
            Schedule (optional)
          </label>
          <input
            id="msg-when"
            type="datetime-local"
            className="field sm:max-w-xs"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
          <p className="mt-1 text-xs text-cream-300/45">
            Leave blank to send now. A scheduled audience is resolved at send time.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 border-t border-ink-800 pt-4">
          <Button disabled={!valid} loading={sendMut.isPending} onClick={() => sendMut.mutate()}>
            {scheduledFor ? 'Schedule message' : `Send to ${preview.data?.count ?? 0}`}
          </Button>
          <Button variant="ghost" disabled={!valid} loading={testMut.isPending} onClick={() => testMut.mutate()}>
            Send a test to myself
          </Button>
        </div>
      </section>

      <div className="space-y-4">
        <TemplatePreview
          label="How it will read"
          subject={textPreview.data?.title ?? title}
          body={textPreview.data?.body ?? body}
          vars={previewVars}
        />
        <p className="px-1 text-xs text-cream-300/45">
          Shown as it would arrive for you. Each member sees their own name; tasting details
          come from the selected tasting.
        </p>
      </div>
    </div>
  );
}

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function eventLabel(e: CommsEventOption) {
  return `${e.title} · ${e.eventMonth ? `${MONTHS[e.eventMonth]} ` : ''}${e.eventYear}${
    e.status === 'upcoming' ? '' : ` (${e.status})`
  }`;
}

// --------------------------------------------------------------- Templates

function Templates() {
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ['comms', 'templates'], queryFn: () => commsApi.templates() });
  const [editing, setEditing] = useState<CommsTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  const reset = useMutation({
    mutationFn: (id: string) => commsApi.resetTemplate(id),
    onSuccess: () => {
      toast.success('Template restored to the original.');
      void qc.invalidateQueries({ queryKey: ['comms', 'templates'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => commsApi.deleteTemplate(id),
    onSuccess: () => {
      toast.success('Template deleted.');
      void qc.invalidateQueries({ queryKey: ['comms', 'templates'] });
    },
  });

  const groups: [string, string, CommsTemplate[]][] = [
    ['event', 'About a tasting', (templates.data ?? []).filter((t) => t.scope === 'event')],
    ['general', 'General letters', (templates.data ?? []).filter((t) => t.scope === 'general')],
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-cream-300/70">
          The standard letters. Built-in ones drive the automatic emails (announcement, RSVP
          reminder) — edit the copy here and the next send uses it.
        </p>
        <Button onClick={() => setCreating(true)}>New template</Button>
      </div>

      {templates.isLoading ? (
        <p className="text-sm text-cream-300/60">Loading…</p>
      ) : (
        groups.map(([key, label, list]) => (
          <section key={key} className="space-y-2">
            <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">{label}</h2>
            {list.length === 0 ? (
              <p className="text-sm text-cream-300/50">None yet.</p>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2">
                {list.map((t) => (
                  <li key={t.id} className="card flex flex-col gap-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-cream-50">{t.name}</p>
                        <p className="truncate text-xs text-cream-300/60">{t.subject}</p>
                      </div>
                      {t.isSystem ? <Badge tone="gold">Built-in</Badge> : <Badge>Custom</Badge>}
                    </div>
                    {t.description ? (
                      <p className="text-xs text-cream-300/60">{t.description}</p>
                    ) : null}
                    <p className="text-[11px] text-cream-300/40">
                      {MESSAGE_CATEGORY_LABELS[t.category as MessageCategory] ?? t.category} ·{' '}
                      {t.channels.join(' + ') || 'inbox only'}
                      {t.updatedByName ? ` · edited by ${t.updatedByName}` : ''}
                    </p>
                    <div className="mt-auto flex flex-wrap gap-2 pt-1">
                      <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setEditing(t)}>
                        Edit
                      </Button>
                      <Link
                        to={`/comms?tab=compose`}
                        className="btn-quiet px-3 py-1 text-xs"
                        onClick={() => sessionStorage.setItem('gwc-compose-template', t.id)}
                      >
                        Use
                      </Link>
                      {t.isSystem ? (
                        <Button
                          variant="quiet"
                          className="px-3 py-1 text-xs"
                          loading={reset.isPending && reset.variables === t.id}
                          onClick={() => reset.mutate(t.id)}
                        >
                          Reset to original
                        </Button>
                      ) : (
                        <Button
                          variant="quiet"
                          className="px-3 py-1 text-xs text-danger"
                          loading={remove.isPending && remove.variables === t.id}
                          onClick={() => remove.mutate(t.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))
      )}

      <TemplateEditor open={creating} onClose={() => setCreating(false)} />
      <TemplateEditor
        open={!!editing}
        onClose={() => setEditing(null)}
        template={editing ?? undefined}
      />
    </div>
  );
}

// ----------------------------------------------------------------- History

function History() {
  const qc = useQueryClient();
  const [eventId, setEventId] = useState('');
  const [origin, setOrigin] = useState<'leadership' | 'all'>('leadership');
  const events = useQuery({ queryKey: ['comms', 'events'], queryFn: () => commsApi.events() });
  const history = useQuery({
    queryKey: ['comms', 'history', eventId, origin],
    queryFn: () => commsApi.history({ eventId: eventId || undefined, origin, limit: 60 }),
    refetchInterval: 15_000, // sends are in flight for a few seconds; keep the rollups honest
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => commsApi.cancel(id),
    onSuccess: () => {
      toast.success('Scheduled message cancelled.');
      void qc.invalidateQueries({ queryKey: ['comms', 'history'] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_220px]">
        <Select value={eventId} onChange={(e) => setEventId(e.target.value)}>
          <option value="">Every tasting and general messages</option>
          {(events.data ?? []).map((e) => (
            <option key={e.id} value={e.id}>
              {eventLabel(e)}
            </option>
          ))}
        </Select>
        <Select value={origin} onChange={(e) => setOrigin(e.target.value as 'leadership' | 'all')}>
          <option value="leadership">Sent by leadership</option>
          <option value="all">Including automatic mail</option>
        </Select>
      </div>

      {history.isLoading ? (
        <p className="text-sm text-cream-300/60">Loading…</p>
      ) : !history.data?.length ? (
        <div className="card p-8 text-center text-sm text-cream-300/50">Nothing sent yet.</div>
      ) : (
        <ul className="space-y-2">
          {history.data.map((m) => (
            <li key={m.id} className="card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <Link to={`/comms/${m.id}`} className="block truncate text-sm text-cream-100 hover:text-gold-200">
                    {m.title}
                  </Link>
                  <p className="mt-0.5 text-xs text-cream-300/50">
                    {m.origin === 'system' ? 'Automatic' : m.senderName ?? 'Leadership'} ·{' '}
                    {m.status === 'scheduled' && m.scheduledFor
                      ? `Scheduled for ${new Date(m.scheduledFor).toLocaleString()}`
                      : m.sentAt
                        ? new Date(m.sentAt).toLocaleString()
                        : `${MESSAGE_STATUS_LABEL[m.status] ?? m.status} · ${new Date(m.createdAt).toLocaleString()}`}
                    {m.eventId ? (
                      <>
                        {' · '}
                        <Link to={`/tastings/${m.eventId}`} className="hover:text-gold-200">
                          tasting
                        </Link>
                      </>
                    ) : null}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <RollupLine m={m} />
                  {m.status === 'scheduled' ? (
                    <button
                      type="button"
                      className="text-xs text-wine-300 hover:text-wine-100"
                      onClick={() => cancelMut.mutate(m.id)}
                    >
                      Cancel
                    </button>
                  ) : null}
                  <Link to={`/comms/${m.id}`} className="btn-quiet px-2 py-1 text-xs">
                    Details
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
